import { User } from 'firebase/auth';

import SectionApi, { NewSectionData } from '../api/sectionApi';
import StatApi from '../api/statApi';
import SectionData from '../types/SectionData';
import SkillData from '../types/skillData';
import { StatType } from '../types/statData';
import { insertInOrder, swapLocations } from '../utils/functions';

export default class SectionService {
  public static async updateSectionInArray(sectionToUpdate: SectionData, sections: SectionData[]) {
    const id = await SectionApi.updateSection(sectionToUpdate);
    const updatedSection = await SectionApi.getSectionById(id);
    return sections.map(section => (section.id === updatedSection.id ? updatedSection : section));
  }

  public static async insertSectionInArray(newSection: NewSectionData, sections: SectionData[]) {
    const id = await SectionApi.addSection(newSection);
    const addedSection = await SectionApi.getSectionById(id);
    const updatedSections = insertInOrder(sections, addedSection);
    await SectionApi.updateSections(updatedSections);
    return updatedSections;
  }

  public static async addSectionLastInArray(newSection: Omit<NewSectionData, 'order'>, sections: SectionData[]) {
    const id = await SectionApi.addSection({ ...newSection, order: sections.length + 1 });
    const addedSection = await SectionApi.getSectionById(id);
    return [...sections, addedSection];
  }

  public static async removeSectionFromArray(sectionToDelete: SectionData, sections: SectionData[]) {
    await SectionApi.removeSection(sectionToDelete);
    const updatedSections = sections
      .filter(section => section.id !== sectionToDelete.id)
      .map((section, i) => ({ ...section, order: i + 1 } as SectionData));
    SectionApi.updateSections(updatedSections);
    return updatedSections;
  }

  public static async getSectionCountBySkill(skillId: string) {
    const sections = await SectionApi.getSectionsBySkillId(skillId);
    return sections.length;
  }

  public static swapSectionsInArray(index1: number, index2: number, sections: SectionData[]) {
    const sectionsCopy = [...sections];
    swapLocations(sectionsCopy, index1, index2);
    const updatedSections = sectionsCopy.map((section, i) => ({ ...section, order: i + 1 } as SectionData));
    SectionApi.updateSections(updatedSections);
    return updatedSections;
  }

  /**
   * Get section that comes after the given section according to order. Returns null if no such section exists.
   * @param section
   */
  static async getNextSection(section: SectionData): Promise<SectionData | null> {
    const sections = await SectionApi.getSectionsBySkillId(section.skillId);
    const sectionIndex = sections.map(section => section.id).indexOf(section.id);
    if (sectionIndex === -1) {
      throw new Error(`Section with id ${section.id} not found among sections`);
    }
    return sectionIndex < sections.length - 1 ? sections[sectionIndex + 1] : null;
  }

  /**
   * Get first section of a skill according to order. Returns null if no such section exists.
   * @param skill
   */
  static async getFirstSection(skill: SkillData): Promise<SectionData | null> {
    const sections = await SectionApi.getSectionsBySkillId(skill.id);
    return sections[0] || null;
  }

  public static async getIncompleteSectionForUser(skill: SkillData, user: User): Promise<SectionData[]> {
    const sections = await SectionApi.getSectionsBySkillId(skill.id);
    const allSectionsStatsForUser = await StatApi.getStatsWithTypeAndUserId(StatType.Section, user.uid);
    const allCompletedSectionsIds = allSectionsStatsForUser.map(stat => stat.targetId);
    return sections.filter(section => !allCompletedSectionsIds.includes(section.id));
  }

  public static async getFirstIncompleteSection(skill: SkillData, user: User | null): Promise<SectionData | null> {
    if (user === null) return null;
    const incompleteSkills = await this.getIncompleteSectionForUser(skill, user);
    return incompleteSkills.length > 0 ? incompleteSkills[0] : null;
  }
}
